
import React, { useState, useContext } from 'react';
import Modal from './Modal';
import Button from '../ui/Button';
import FileUpload from '../ui/FileUpload';
import { getAxiosTokenInstance } from '../../../utils/axiosInstance';
import { message } from 'antd';
import { NecttosContext } from '../../../context/NecttosContext';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
;


interface MaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: MaterialData) => void;
  subjectId: string,
  modules: any[]
}

interface MaterialData {
  title: string;
  description: string;
  attachments: {name: string, url: string, size: Number, type: string};
  moduleId: string,
  subjectId: string
}

const MaterialModal: React.FC<MaterialModalProps> = ({ isOpen, onClose, onSubmit, subjectId, modules }) => {
  const [formData, setFormData] = useState<MaterialData>({
    title: '',
    description: '',
    attachments: {name: '', url: '', size: 0, type: ''},
    moduleId: "",
    subjectId: subjectId
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const { collegeId, userId } = useContext(NecttosContext)

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Material title is required';
    }

    // if (formData.attachments.length === 0) {
    //   newErrors.attachments = 'At least one file must be uploaded';
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleFileUpload = async (file: File) => {
    // const file = event.target.files ? event.target.files[0] : null;
  
    if (!file) {
      message.error('No file selected.');
      return;
    }
  
    if (file.size > 10 * 1024 * 1024) {
      message.error(`File size must be less than 10 MB.`);
      return;
    }
  
    setIsUploading(true);
  
    try {
      const downloadURL = await uploadFileToFirebase(file);
      if (downloadURL) {
       setFormData(prev => ({
  ...prev,
  attachments: {
    name: file.name,
    url: downloadURL,
    size: file.size,
    type: file.type
  }
}));


        message.success(`${file.name} uploaded successfully.`);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      message.error('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };
  
  
    const uploadFileToFirebase = async (file: File): Promise<string | null> => {
    const storage = getStorage();
    const filePath = `materials/${subjectId}/${formData.moduleId}/${file.name}`;
    const fileRef = ref(storage, filePath);
  
    const snapshot = await uploadBytes(fileRef, file, {
      contentType: file.type
    });
  
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  };
  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
   const instance = await getAxiosTokenInstance()
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const resp = await instance.post(`/college/lms/materials/createMaterial`, {...formData, collegeId:collegeId});
      if(resp?.data){
        console.log(resp?.data.message)
       message.success('Material added')
      }
      handleReset();
      onClose();
    } catch (error) {
      console.error('Error creating learning material:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      title: '',
      description: '',
      attachments: {name: '', type: '', size: 0, url: ''},
      moduleId: "",
      subjectId: subjectId
    });
    setErrors({});
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Learning Material" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title */}
        <div>
          <label htmlFor="materialTitle" className="block text-sm font-medium text-gray-700 mb-2">
            📁 Material Title *
          </label>
          <input
            type="text"
            id="materialTitle"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.title ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter material title"
          />
          {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description (Optional)
          </label>
          <textarea
            id="description"
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter material description and usage instructions..."
          />
        </div>


        <label htmlFor="module" className="block text-sm font-medium text-gray-700 mb-2">
    Module *
  </label>

  <select
    id="module"
    value={formData.moduleId}
    onChange={(e) => setFormData(prev => ({ ...prev, moduleId: e.target.value }))}
    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
      errors.module ? 'border-red-500' : 'border-gray-300'
    }`}
  >
    <option value="">Select a module</option>
    {modules?.map((mod) => (
      <option key={mod._id} value={mod._id}>
        {mod.fullModuleName}
      </option>
    ))}
  </select>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Learning Materials *
          </label>
          <FileUpload
  onFilesSelected={async (files: File[]) => {
    const file = files[0];
   
   handleFileUpload(file);
  }}
  label="Upload assignment materials"
/>

          {errors.attachments && <p className="mt-1 text-sm text-red-600">{errors.attachments}</p>}
          
          <div className="mt-2 text-xs text-gray-500">
            <p>Supported formats:</p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Documents: PDF, Word, PowerPoint, Excel</li>
              <li>Media: Images, Videos, Audio files</li>
              <li>Text files and other educational resources</li>
              <li>Maximum file size: 50MB per file</li>
            </ul>
          </div>
        </div>

        {/* File Preview */}
        {/* {formData.attachments.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Files:</h4>
            <div className="space-y-2">
              {formData.attachments.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="text-2xl">
                      {file.type.startsWith('image/') ? '🖼' : 
                       file.type.startsWith('video/') ? '🎥' :
                       file.type.startsWith('audio/') ? '🎵' :
                       file.type.includes('pdf') ? '📄' :
                       file.type.includes('word') ? '📝' :
                       file.type.includes('powerpoint') ? '📊' :
                       file.type.includes('excel') ? '📈' : '📁'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-500">
                       {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newFiles = formData.attachments.filter((_, i) => i !== index);
                      setFormData(prev => ({ ...prev, attachments: newFiles }));
                    }}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Remove file"
                  >
                    ✖
                  </button>
                </div>
              ))}
            </div>
          </div>
        )} */}

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <span className="flex items-center">
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Uploading...
              </span>
            ) : (
              'Add Material'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default MaterialModal;
